import React from 'react';
import styled, { css } from 'styled-components';
import { ElementFrame, PitchSlide, SlideBackground } from '../data/slides';
import { getLayerFrame, resolveLayerOrder, zIndexForLayer } from '../data/layerOrder';
import { getComponentById, isComponentId } from '../data/slideComponents';
import { loadActiveDesign } from '../data/designStore';
import { headingFont } from '../styles/fontStyles';
import { pitchTheme } from '../styles/theme';
import SlideLogo from './SlideLogo';
import SlideComponentView from './SlideComponentView';
import TapePhoto from './TapePhoto';

// SVG data URIs cannot use CSS variables, so the torn edge takes its fill
// color from the active design at render time.
const tornEdgeSvg = (color: string): string =>
  encodeURIComponent(`
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 40 800" preserveAspectRatio="none">
  <path fill="${color}" d="M0,0 L28,0 L22,40 L30,80 L18,120 L32,160 L20,200 L34,240 L16,280 L30,320 L22,360 L28,400 L20,440 L32,480 L18,520 L30,560 L22,600 L28,640 L20,680 L32,720 L18,760 L26,800 L0,800 Z"/>
</svg>
`);

const Card = styled.article<{ $imageOnRight: boolean; $pdfCapture?: boolean }>`
  position: relative;
  display: grid;
  grid-template-columns: 1fr 1fr;
  min-height: 520px;
  width: min(920px, 100%);
  border: 1px solid ${pitchTheme.border};
  border-radius: 18px;
  overflow: hidden;
  background: ${pitchTheme.cream};
  box-shadow: 0 16px 48px rgba(0, 0, 0, 0.08);

  ${({ $pdfCapture }) =>
    $pdfCapture &&
    css`
      width: 920px;
      height: 520px;
      min-height: 520px;
      box-shadow: none;
    `}

  ${({ $imageOnRight }) =>
    $imageOnRight
      ? css`
          .content-panel {
            order: 1;
          }
          .image-panel {
            order: 2;
          }
        `
      : css`
          .content-panel {
            order: 2;
          }
          .image-panel {
            order: 1;
          }
        `}

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    min-height: auto;

    .content-panel,
    .image-panel {
      order: unset !important;
    }
  }
`;

const ContentPanel = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 40px 36px 40px 48px;
  background: ${pitchTheme.cream};

  @media (max-width: 768px) {
    padding: 32px 24px;
  }
`;

const ImagePanel = styled.div<{
  $tornEdgeSide: 'left' | 'right';
  $edgeColor: string;
  $pdfCapture?: boolean;
}>`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 48px 32px;
  background: ${pitchTheme.gradient};

  ${({ $pdfCapture, $edgeColor, $tornEdgeSide }) =>
    !$pdfCapture &&
    css`
      &::before {
        content: '';
        position: absolute;
        top: 0;
        bottom: 0;
        width: 40px;
        background-image: url('data:image/svg+xml,${tornEdgeSvg($edgeColor)}');
        background-repeat: repeat-y;
        background-size: 100% 100%;
        z-index: 1;
        left: ${$tornEdgeSide === 'left' ? '-1px' : 'auto'};
        right: ${$tornEdgeSide === 'right' ? '-1px' : 'auto'};
        transform: ${$tornEdgeSide === 'left' ? 'scaleX(-1)' : 'none'};
      }
    `}

  @media (max-width: 768px) {
    padding: 32px 24px 40px;

    &::before {
      display: none;
    }
  }
`;

const Logo = styled.div`
  margin-bottom: 24px;
`;

export const CardHeading = styled.h2`
  margin: 0 0 24px;
  ${headingFont}
  font-size: clamp(1.5rem, 3vw, 2rem);
  line-height: 1.25;
  letter-spacing: -0.02em;
  color: ${pitchTheme.text};
`;

export const CardHighlightList = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-width: 38ch;
`;

export const CardHighlightItem = styled.li`
  position: relative;
  padding-left: 18px;
  font-family: ${pitchTheme.fontContent};
  font-size: 0.9375rem;
  font-weight: 500;
  line-height: 1.5;
  color: ${pitchTheme.textMuted};

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0.55em;
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: ${pitchTheme.blue};
    transform: translateY(-50%);
  }
`;

/* ---------- Freeform layout rendering ---------- */

const frameToStyle = (frame: ElementFrame): React.CSSProperties => ({
  position: 'absolute',
  left: `${frame.x}%`,
  top: `${frame.y}%`,
  width: `${frame.w}%`,
  height: `${frame.h}%`,
});

const FreeformCard = styled.article<{ $pdfCapture?: boolean }>`
  position: relative;
  width: min(920px, 100%);
  aspect-ratio: 920 / 520;
  border: 1px solid ${pitchTheme.border};
  border-radius: 18px;
  overflow: hidden;
  background: ${pitchTheme.cream};
  box-shadow: 0 16px 48px rgba(0, 0, 0, 0.08);

  ${({ $pdfCapture }) =>
    $pdfCapture &&
    css`
      width: 920px;
      height: 520px;
      aspect-ratio: auto;
      box-shadow: none;
    `}
`;

export const GradientHalf = styled.div<{
  $side: 'left' | 'right';
  $edgeColor: string;
  $pdfCapture?: boolean;
}>`
  position: absolute;
  top: 0;
  bottom: 0;
  width: 50%;
  background: ${pitchTheme.gradient};
  left: ${({ $side }) => ($side === 'left' ? 0 : 'auto')};
  right: ${({ $side }) => ($side === 'right' ? 0 : 'auto')};

  ${({ $pdfCapture, $edgeColor, $side }) =>
    !$pdfCapture &&
    css`
      &::before {
        content: '';
        position: absolute;
        top: 0;
        bottom: 0;
        width: 40px;
        background-image: url('data:image/svg+xml,${tornEdgeSvg($edgeColor)}');
        background-repeat: repeat-y;
        background-size: 100% 100%;
        left: ${$side === 'right' ? '-1px' : 'auto'};
        right: ${$side === 'left' ? '-1px' : 'auto'};
        transform: ${$side === 'right' ? 'scaleX(-1)' : 'none'};
      }
    `}
`;

export const SlideBackgroundLayer: React.FC<{
  background?: SlideBackground;
  pdfCapture?: boolean;
}> = ({ background, pdfCapture }) => {
  const edgeColor = loadActiveDesign().palette.blue;
  if (background === 'imageRight') {
    return <GradientHalf $side="right" $edgeColor={edgeColor} $pdfCapture={pdfCapture} />;
  }
  if (background === 'imageLeft') {
    return <GradientHalf $side="left" $edgeColor={edgeColor} $pdfCapture={pdfCapture} />;
  }
  return null;
};

const ElementBox = styled.div<{ $zIndex: number }>`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  overflow: hidden;
  z-index: ${({ $zIndex }) => $zIndex};
`;

const CenteredElementBox = styled(ElementBox)`
  align-items: center;
  justify-content: center;
`;

interface PitchCardProps {
  slide: PitchSlide;
  imageOnRight: boolean;
  logoUrl?: string | null;
  pdfCapture?: boolean;
}

const FreeformPitchCard: React.FC<{
  slide: PitchSlide;
  logoUrl?: string | null;
  pdfCapture?: boolean;
}> = ({ slide, logoUrl, pdfCapture }) => {
  const layout = slide.layout!;
  const layerOrder = resolveLayerOrder(layout);

  const renderElement = (layerId: typeof layerOrder[number]) => {
    const frame = getLayerFrame(layout, layerId);
    if (!frame) {
      return null;
    }

    const layerIndex = layerOrder.indexOf(layerId);
    const frameStyle = frameToStyle(frame);
    const zIndex = zIndexForLayer(layerIndex);

    if (isComponentId(layerId)) {
      const component = getComponentById(layout, layerId);
      if (!component) {
        return null;
      }
      return (
        <ElementBox key={layerId} $zIndex={zIndex} style={frameStyle}>
          <SlideComponentView component={component} />
        </ElementBox>
      );
    }

    if (layerId === 'logo') {
      return (
        <ElementBox key={layerId} $zIndex={zIndex} style={frameStyle}>
          <SlideLogo logoUrl={logoUrl} />
        </ElementBox>
      );
    }

    if (layerId === 'title') {
      return (
        <ElementBox key={layerId} $zIndex={zIndex} style={frameStyle}>
          <CardHeading>{slide.title}</CardHeading>
        </ElementBox>
      );
    }

    if (layerId === 'highlights') {
      return (
        <ElementBox key={layerId} $zIndex={zIndex} style={frameStyle}>
          <CardHighlightList>
            {slide.highlights.map((point) => (
              <CardHighlightItem key={point}>{point}</CardHighlightItem>
            ))}
          </CardHighlightList>
        </ElementBox>
      );
    }

    return (
      <CenteredElementBox key={layerId} $zIndex={zIndex} style={frameStyle}>
        <TapePhoto
          src={slide.imageUrl}
          alt={slide.imageAlt}
          borderRadius={layout.imageStyle?.borderRadius}
          fillFrame
          showTape={false}
        />
      </CenteredElementBox>
    );
  };

  return (
    <FreeformCard aria-label={slide.title} data-pdf-slide $pdfCapture={pdfCapture}>
      <SlideBackgroundLayer background={layout.background} pdfCapture={pdfCapture} />
      {layerOrder.map(renderElement)}
    </FreeformCard>
  );
};

const PitchCard: React.FC<PitchCardProps> = ({ slide, imageOnRight, logoUrl, pdfCapture }) => {
  if (slide.layout) {
    return <FreeformPitchCard slide={slide} logoUrl={logoUrl} pdfCapture={pdfCapture} />;
  }

  return (
    <Card
      $imageOnRight={imageOnRight}
      $pdfCapture={pdfCapture}
      aria-label={slide.title}
      data-pdf-slide
    >
      <ContentPanel className="content-panel">
        <Logo>
          <SlideLogo logoUrl={logoUrl} />
        </Logo>
        <CardHeading>{slide.title}</CardHeading>
        <CardHighlightList>
          {slide.highlights.map((point) => (
            <CardHighlightItem key={point}>{point}</CardHighlightItem>
          ))}
        </CardHighlightList>
      </ContentPanel>
      <ImagePanel
        className="image-panel"
        $tornEdgeSide={imageOnRight ? 'left' : 'right'}
        $edgeColor={loadActiveDesign().palette.blue}
        $pdfCapture={pdfCapture}
      >
        <TapePhoto src={slide.imageUrl} alt={slide.imageAlt} />
      </ImagePanel>
    </Card>
  );
};

export default PitchCard;
